import React, { useState, useEffect } from 'react';
import { 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import LeadsService from '../services/leadsService';

const AutoCaptureForm = () => {
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    contactNumber: '',
    tourPackage: '',
    travelDate: '',
    source: 'website'
  });

  const [tourPackages, setTourPackages] = useState([]);
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  // Load tour packages
  useEffect(() => {
    const loadTourPackages = async () => {
      try {
        console.log('🔍 Loading tour packages...');
        // Use public endpoint that doesn't require authentication
        const response = await fetch('/tour-packages/public');
        console.log('📡 Response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('📦 Packages data:', data);
          const packages = data.packages || data || [];
          console.log('✅ Loaded packages:', packages.length);
          setTourPackages(packages);
        } else {
          console.error('❌ Failed to load packages:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('❌ Error loading tour packages:', error);
      }
    };

    loadTourPackages();

    // Auto-populate source from URL
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get('source');
    if (sourceParam) {
      setFormData(prev => ({ ...prev, source: sourceParam }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      // Get source and campaign from URL
      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('source') || 'website';
      const campaign = urlParams.get('campaign') || '';
      
      // Get selected package details
      const selectedPkg = tourPackages.find(p => p._id === formData.tourPackage);
      
      const leadData = {
        customerName: formData.customerName,
        name: formData.customerName,
        email: formData.email,
        contactNumber: formData.contactNumber,
        phone: formData.contactNumber,
        tourPackage: formData.tourPackage,
        destination: selectedPkg?.destination || '',
        numberOfDays: selectedPkg?.duration?.days || null,
        travelDate: formData.travelDate,
        source: source,
        sourceOfLead: source,
        status: 'New',
        autoCaptured: true,
        captureSource: {
          platform: source,
          campaign: campaign,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date()
        }
      };

      const result = await LeadsService.createLead(leadData);
      
      if (result.success) {
        setStatus('success');
        setMessage('Thank you! We will contact you soon regarding your tour inquiry.');
        
        // Reset form
        setFormData({
          customerName: '',
          email: '',
          contactNumber: '',
          tourPackage: '',
          travelDate: '',
          source: source
        });

        // Redirect after 3 seconds
        setTimeout(() => {
          window.location.href = '/thank-you';
        }, 3000);
      } else {
        setStatus('error');
        setMessage(result.error || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      setStatus('error');
      setMessage('An error occurred. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🎒 Plan Your Dream Tour
          </h1>
          <p className="text-gray-600">
            Fill in your details and we'll help you plan the perfect trip!
          </p>
        </div>

        {status === 'success' ? (
          <div className="text-center py-8">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-green-600 mb-2">Success!</h3>
            <p className="text-gray-600">{message}</p>
            <p className="text-sm text-gray-500 mt-4">Redirecting...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Customer Name */}
            <div>
              <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-1">
                👤 Your Name *
              </label>
              <input
                type="text"
                name="customerName"
                id="customerName"
                required
                value={formData.customerName}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your full name"
              />
            </div>

            {/* Contact Number */}
            <div>
              <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-1">
                📞 Contact Number *
              </label>
              <input
                type="tel"
                name="contactNumber"
                id="contactNumber"
                required
                value={formData.contactNumber}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your phone number"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                ✉️ Email Address *
              </label>
              <input
                type="email"
                name="email"
                id="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email"
              />
            </div>

            {/* Tour Package */}
            <div>
              <label htmlFor="tourPackage" className="block text-sm font-medium text-gray-700 mb-1">
                🎒 Select Tour Package *
              </label>
              <select
                name="tourPackage"
                id="tourPackage"
                required
                value={formData.tourPackage}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Choose a tour package</option>
                {tourPackages.map((pkg) => (
                  <option key={pkg._id} value={pkg._id}>
                    {pkg.packageName} - {pkg.destination} ({pkg.duration?.days || 0}D/{pkg.duration?.nights || 0}N)
                  </option>
                ))}
              </select>
            </div>

            {/* Travel Date */}
            <div>
              <label htmlFor="travelDate" className="block text-sm font-medium text-gray-700 mb-1">
                📅 Preferred Travel Date *
              </label>
              <input
                type="date"
                name="travelDate"
                id="travelDate"
                required
                value={formData.travelDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Error Message */}
            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{message}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={status === 'loading'}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 ${
                status === 'loading'
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
              }`}
            >
              {status === 'loading' ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </span>
              ) : (
                '🚀 Get Tour Details'
              )}
            </button>

            <p className="text-xs text-center text-gray-500 mt-4">
              By submitting, you agree to receive tour information via email and phone.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default AutoCaptureForm;
